import { useEffect, useRef, useState, type DependencyList, type UIEvent } from 'react';

/**
 * Tracks whether a horizontally-scrollable element is at its start/end edge so
 * callers can fade whichever side still has off-screen content. Measures on
 * mount, on scroll, and on resize. Shared by the search gear chips and the
 * trending gear chips so the detection logic can't drift between them.
 *
 * `deps` re-measures when the scrollable content changes (e.g. chip list).
 */
export function useScrollFade(deps: DependencyList = []) {
  const ref = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const measure = (el: HTMLDivElement | null) => {
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollWidth - el.clientWidth - el.scrollLeft <= 1);
  };

  useEffect(() => {
    measure(ref.current);
    const onResize = () => measure(ref.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const onScroll = (e: UIEvent<HTMLDivElement>) => measure(e.currentTarget);

  return { ref, onScroll, atStart, atEnd };
}

/**
 * Standard symmetric edge-fade mask built from the hook's edge state. Fades
 * whichever side has more to scroll; returns `undefined` when nothing
 * overflows (so a row that fits isn't clipped). For surfaces with a fixed
 * left-aligned overlay (e.g. a filter button), build the mask inline instead.
 */
export function scrollFadeMask(atStart: boolean, atEnd: boolean, size = 24) {
  const fadeLeft = !atStart;
  const fadeRight = !atEnd;
  if (!fadeLeft && !fadeRight) return undefined;
  const gradient = `linear-gradient(to right, ${
    fadeLeft ? `transparent, #000 ${size}px` : '#000'
  }, ${fadeRight ? `#000 calc(100% - ${size}px), transparent` : '#000'})`;
  return { maskImage: gradient, WebkitMaskImage: gradient };
}
